import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd, articleJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";

const URL = "https://alkoholprovet.se/kunskapsbank/kunskapsprovet-orebro";

const faqs = [
  { q: "Var gör jag kunskapsprovet i Örebro?", a: "Örebro kommuns tillståndsenhet på Socialförvaltningen administrerar provet, som skrivs på plats i Stadsbyggnadshuset eller annan utsedd lokal. Tid bokas i samband med ansökan via kommunens e-tjänst." },
  { q: "Hur lång är väntetiden i Örebro?", a: "Normalt 3–6 veckor från ansökan till provtillfälle. Sommarperioden (maj–augusti) kan ge upp till 8 veckors väntetid." },
  { q: "Vad kostar kunskapsprovet i Örebro?", a: "Ansökningsavgift för stadigvarande tillstånd är ca 10 000–12 000 kr. Avgift för enbart omprov ligger på 1 500–2 500 kr." },
  { q: "Kan jag göra provet på engelska i Örebro?", a: "Ja, Örebro erbjuder provet på engelska. Begäran ska lämnas vid bokningen. Vid behov av annan språkstöd kan tolk anlitas på sökandens bekostnad." },
  { q: "Hur lång är handläggningstiden i Örebro?", a: "Räkna med 2–4 månader från komplett ansökan till beslut om stadigvarande tillstånd. Tillfälliga tillstånd hanteras vanligen inom 4–6 veckor." },
];

const sections = [
  { h2: "Tillståndsenheten i Örebro – så fungerar den", body: "Örebro kommuns tillståndsenhet är en del av Socialförvaltningen och ansvarar för både handläggning av serveringstillstånd och kunskapsprovet. Enheten samordnar yttranden från Polisen, Skatteverket, Räddningstjänsten och Miljökontoret innan beslut fattas. Du lämnar in ansökan via kommunens e-tjänst på orebro.se, betalar avgiften och bokar kunskapsprov i samma flöde. Som medelstor stad har Örebro generellt kortare handläggningstider än Stockholm, Göteborg och Malmö." },
  { h2: "Ansökan steg för steg", body: "1. Förbered grundhandlingar – bolagsuppgifter, hyreskontrakt, ritning av lokalen, finansiering, PBI-uppgifter.\n2. Logga in på orebro.se med BankID och lämna in ansökan om serveringstillstånd.\n3. Betala ansökningsavgift (10 000–12 000 kr för stadigvarande).\n4. Boka kunskapsprov via e-tjänsten – väntetid 3–6 veckor.\n5. Genomför provet på plats (90 min, papper).\n6. Vänta in remissvar från polis, skatteverket och övriga.\n7. Få beslut – normalt inom 2–4 månader från komplett ansökan." },
  { h2: "Väntetider och säsong", body: "Örebro har generellt smidigare handläggning än storstäderna, men säsongseffekter finns:\n• Januari–mars: snabbast, ofta inom 6 veckor.\n• April–juni: längre köer pga sommaransökningar (8–10 veckor).\n• September–november: medel (6–8 veckor).\n• December: kort månad ger fördröjning.\n\nFör verksamhet inför sommarsäsongen bör ansökan vara inne senast januari." },
  { h2: "Kostnader i Örebro", body: "Typiska avgifter:\n• Stadigvarande tillstånd till allmänheten: 10 000–12 000 kr.\n• Stadigvarande till slutet sällskap: 6 000–8 000 kr.\n• Tillfälligt tillstånd: 2 000–6 000 kr.\n• Omprov kunskapsprov: 1 500–2 500 kr.\n• Årlig tillsynsavgift: 3 000–10 000 kr beroende på alkoholomsättning.\n\nAvgifterna fastställs av kommunfullmäktige och justeras årligen." },
  { h2: "Praktiskt om provdagen i Örebro", body: "Kunskapsprovet hålls på papper i en lugn lokal under tystnad. Du har 90 minuter på dig. Tag med giltig legitimation. Mobil, anteckningar och andra hjälpmedel är inte tillåtna. Resultatet meddelas skriftligt inom 1–2 veckor. Vid godkänt prov fortsätter handläggningen automatiskt; vid underkänt kan du boka omprov tidigast efter 14 dagar mot ny avgift." },
  { h2: "Tips för Örebro-sökande", body: "Örebros tillståndsenhet uppskattar tydliga och kompletta ansökningar. Vanliga orsaker till fördröjning:\n• Otydlig PBI-redogörelse.\n• Brister i finansieringsplan.\n• Ritning utan utmärkta serveringsytor.\n• Saknad bokning av kunskapsprov.\n\nBoka kunskapsprovet direkt vid ansökan så minimerar du den totala tiden." },
];

function Page() {
  return (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Kunskapsbank", to: "/kunskapsbank" },
        { label: "Kunskapsprovet Örebro" },
      ]}
      title="Kunskapsprovet serveringstillstånd Örebro – guide 2026"
      intro="Allt om kunskapsprovet för serveringstillstånd i Örebro: ansökan, väntetider, kostnader och praktiska tips. Uppdaterat för 2026 års regler inklusive slopat matkrav."
      sections={sections}
      faqs={faqs}
      lastReviewed="2026-06-15"
      related={{
        title: "Läs vidare",
        links: [
          { to: "/serveringstillstand-orebro", label: "Serveringstillstånd Örebro – fullständig guide" },
          { to: "/kunskapsbank/kunskapsprovet-stockholm", label: "Kunskapsprovet Stockholm" },
          { to: "/kunskapsbank/kunskapsprovet-jonkoping", label: "Kunskapsprovet Jönköping" },
          { to: "/kunskapsbank/kunskapsprovet-umea", label: "Kunskapsprovet Umeå" },
          { to: "/kunskapsbank/hur-lang-tid-tar-serveringstillstand", label: "Hur lång tid tar tillstånd?" },
          { to: "/practice", label: "Träna på kunskapsprovet" },
        ],
      }}
      sources={[
        { label: "Örebro kommun – serveringstillstånd", href: "https://www.orebro.se/" },
        { label: "Alkohollagen (2010:1622)", href: "https://www.riksdagen.se/sv/dokument-och-lagar/dokument/svensk-forfattningssamling/alkohollag-20101622_sfs-2010-1622/" },
      ]}
    />
  );
}

export const Route = createFileRoute("/kunskapsbank/kunskapsprovet-orebro")({
  component: Page,
  head: () => ({
    meta: [
      { title: "Kunskapsprovet Örebro – ansökan, väntetid & kostnad 2026" },
      { name: "description", content: "Allt om kunskapsprovet för serveringstillstånd i Örebro: ansökan, väntetider, kostnader och tips. Uppdaterat 2026." },
      { property: "og:title", content: "Kunskapsprovet Örebro – komplett guide 2026" },
      { property: "og:description", content: "Allt om kunskapsprovet för serveringstillstånd i Örebro." },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Kunskapsbank", url: "https://alkoholprovet.se/kunskapsbank" },
        { label: "Kunskapsprovet Örebro", url: URL },
      ]),
      articleJsonLd({
        url: URL,
        headline: "Kunskapsprovet Örebro 2026",
        description: "Komplett guide till kunskapsprovet för serveringstillstånd i Örebro.",
        datePublished: "2026-06-15",
        dateModified: "2026-06-15",
      }),
    ],
  }),
});
